import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, ExternalLink, Code2, Eye, Copy, Check, Maximize2, Minimize2 } from 'lucide-react';

/**
 * HtmlArtifactPanel — slide-out drawer that previews assistant-generated
 * HTML in a sandboxed iframe (like Claude's Artifacts). Provides:
 *   - Preview / Code toggle
 *   - Download as .html
 *   - Copy to clipboard
 *   - Open in new tab
 *   - Fullscreen toggle
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   html: string                  — the inner HTML body to render
 *   title?: string                — drawer header label
 *   conversationId?: string       — used as filename hint for download
 */
export const HtmlArtifactPanel = ({ open, onClose, html, title, conversationId }) => {
  const [view, setView] = useState('preview'); // 'preview' | 'code'
  const [copied, setCopied] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  // If the assistant emitted a complete HTML document (with `<html>`),
  // use it as-is. Otherwise wrap the fragment so the iframe still
  // renders with a sane base styling.
  const docHtml = useMemo(() => {
    const trimmed = (html || '').trim();
    if (/^<!doctype html>/i.test(trimmed) || /<html[\s>]/i.test(trimmed)) {
      return trimmed;
    }
    return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>${(title || 'Cognara Artifact').replace(/[<>"']/g, '')}</title>
  <style>
    body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1f2937; line-height: 1.55; padding: 24px; background: #ffffff; }
    h1,h2,h3 { margin-top: 0; color: #111827; }
    a { color: #1E4E8C; }
    table { border-collapse: collapse; width: 100%; }
    th,td { padding: 8px 12px; border: 1px solid #e5e7eb; text-align: left; }
    th { background: #f3f4f6; }
    pre,code { font-family: ui-monospace, "SF Mono", Menlo, monospace; }
    pre { background: #f3f4f6; padding: 12px; border-radius: 6px; overflow:auto; }
    button { cursor: pointer; }
  </style>
</head>
<body>
${trimmed}
</body>
</html>`;
  }, [html, title]);

  // ESC closes the drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleDownload = () => {
    const blob = new Blob([docHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().slice(0, 10);
    const slug = (title || 'cognara-artifact').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40) || 'artifact';
    a.href = url;
    a.download = `${slug}-${ts}.html`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(docHtml);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  const handleOpenTab = () => {
    const blob = new Blob([docHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
            data-testid="html-artifact-backdrop"
          />
          <motion.aside
            initial={{ x: 720, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 720, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
            className={`fixed top-0 right-0 z-50 h-full bg-nexus-bg-primary border-l border-nexus-border-subtle shadow-2xl flex flex-col ${
              fullscreen ? 'w-full' : 'w-[720px] max-w-[96vw]'
            }`}
            data-testid="html-artifact-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-nexus-border-subtle">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] text-nexus-text-tertiary mb-0.5">Interactive Artifact</p>
                <h2 className="text-sm font-semibold text-nexus-text-primary truncate">
                  {title || 'HTML Preview'}
                </h2>
              </div>
              <div className="flex items-center gap-1">
                {/* View toggle */}
                <div className="flex rounded-lg overflow-hidden" style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)' }}>
                  <button
                    onClick={() => setView('preview')}
                    className={`px-2.5 py-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold ${
                      view === 'preview' ? 'text-white' : 'text-nexus-text-tertiary'
                    }`}
                    style={view === 'preview' ? { background: 'linear-gradient(135deg, #6BA6F5, #1E4E8C)' } : {}}
                    data-testid="artifact-view-preview"
                  >
                    <Eye size={12} /> Preview
                  </button>
                  <button
                    onClick={() => setView('code')}
                    className={`px-2.5 py-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold ${
                      view === 'code' ? 'text-white' : 'text-nexus-text-tertiary'
                    }`}
                    style={view === 'code' ? { background: 'linear-gradient(135deg, #6BA6F5, #1E4E8C)' } : {}}
                    data-testid="artifact-view-code"
                  >
                    <Code2 size={12} /> Code
                  </button>
                </div>
                <ToolbarBtn onClick={handleCopy} title="Copy HTML" testid="artifact-copy">
                  {copied ? <Check size={14} className="text-nexus-accent-mint" /> : <Copy size={14} />}
                </ToolbarBtn>
                <ToolbarBtn onClick={handleDownload} title="Download .html" testid="artifact-download">
                  <Download size={14} />
                </ToolbarBtn>
                <ToolbarBtn onClick={handleOpenTab} title="Open in new tab" testid="artifact-open-tab">
                  <ExternalLink size={14} />
                </ToolbarBtn>
                <ToolbarBtn onClick={() => setFullscreen((v) => !v)} title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'} testid="artifact-fullscreen">
                  {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </ToolbarBtn>
                <ToolbarBtn onClick={onClose} title="Close" testid="artifact-close">
                  <X size={14} />
                </ToolbarBtn>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-hidden bg-nexus-bg-secondary/40">
              {view === 'preview' ? (
                <iframe
                  title="Cognara HTML artifact preview"
                  srcDoc={docHtml}
                  // `allow-scripts` lets generated dashboards / interactive
                  // widgets run JS; `allow-same-origin` is deliberately
                  // omitted so the iframe can't reach our session cookies.
                  sandbox="allow-scripts allow-forms allow-popups allow-modals"
                  className="w-full h-full bg-white"
                  data-testid="artifact-preview-iframe"
                />
              ) : (
                <pre className="w-full h-full m-0 p-4 overflow-auto text-[12px] leading-relaxed text-nexus-text-primary font-mono"
                     style={{ background: 'var(--layer-2)' }}
                     data-testid="artifact-code-pane">
                  <code>{docHtml}</code>
                </pre>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-nexus-border-subtle flex items-center justify-between text-[10px] text-nexus-text-tertiary">
              <span className="uppercase tracking-wider">
                {docHtml.length.toLocaleString()} chars · sandboxed iframe · scripts allowed
              </span>
              <span className="font-mono opacity-60">{(conversationId || '').slice(0, 8)}</span>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

const ToolbarBtn = ({ onClick, title, testid, children }) => (
  <button
    onClick={onClick}
    title={title}
    className="w-7 h-7 rounded-md inline-flex items-center justify-center text-nexus-text-tertiary hover:text-nexus-text-primary hover:bg-white/5 transition-colors"
    data-testid={testid}
  >
    {children}
  </button>
);

export default HtmlArtifactPanel;
